import React, { createContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [mfaVerified, setMfaVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (authId) => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('auth_id', authId)
      .single();
    if (!error && data) setProfile(data);
    // A session whose auth id has NO users row is a dead session — the
    // account was deleted (or, in demo, the nightly reset dropped a
    // non-persona user). Without this, ProtectedRoute spins forever on
    // `session && !profile`. PGRST116 is .single()'s definitive "0 rows";
    // transient/network errors must NOT sign anyone out.
    if (error?.code === 'PGRST116') {
      await supabase.auth.signOut();
    }
    return data;
  }, []);

  const checkMfaStatus = useCallback(async () => {
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (!data) return false;
    const verified = data.currentLevel === 'aal2' || data.nextLevel === 'aal1';
    setMfaVerified(verified);
    return verified;
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        await fetchProfile(s.user.id);
        await checkMfaStatus();
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, s) => {
        setSession(s);
        setUser(s?.user ?? null);
        if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && s?.user) {
          // Fire and forget — don't block the auth state change callback
          fetchProfile(s.user.id).catch(() => {});
          checkMfaStatus().catch(() => {});
        }
        if (event === 'SIGNED_OUT') {
          setProfile(null);
          setMfaVerified(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [fetchProfile, checkMfaStatus]);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) {
      // Fire-and-forget login-activity stamp — must never block sign-in.
      import('../lib/api').then(({ api }) => api.recordLogin()).catch(() => {});
    }
    return { data, error };
  };

  const signUp = async (email, password, metadata) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata },
    });
    return { data, error };
  };

  const signOut = async () => {
    // Clear any user-specific local state so a different user signing in
    // next doesn't inherit the previous user's org selection.
    try { localStorage.removeItem('pi-portal-current-org'); } catch {}
    await supabase.auth.signOut();
  };

  const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { data, error };
  };

  const updatePassword = async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    return { data, error };
  };

  // Recovery links carry a single-use token hash. Exchanging it here — in the
  // browser, on user arrival — is what keeps mail-scanner prefetches (Microsoft
  // Defender Safe Links and friends) from burning the token before the human
  // ever clicks: a scanner fetching the SPA URL runs no JS, so the token
  // survives. See database/supabase-email-templates/reset-password.html.
  const verifyRecoveryToken = async (tokenHash, type = 'recovery') => {
    const { data, error } = await supabase.auth.verifyOtp({
      type,
      token_hash: tokenHash,
    });
    return { data, error };
  };

  // PKCE-shaped links (?code=...) — not what the current templates emit, but
  // cheap to support so a future flowType change doesn't silently break resets.
  const exchangeRecoveryCode = async (code) => {
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    return { data, error };
  };

  const mfaEnroll = async () => {
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
    return { data, error };
  };

  const mfaChallenge = async (factorId) => {
    const { data, error } = await supabase.auth.mfa.challenge({ factorId });
    return { data, error };
  };

  const mfaVerify = async (factorId, challengeId, code) => {
    const { data, error } = await supabase.auth.mfa.verify({ factorId, challengeId, code });
    if (!error) setMfaVerified(true);
    return { data, error };
  };

  const mfaUnenroll = async (factorId) => {
    const { data, error } = await supabase.auth.mfa.unenroll({ factorId });
    return { data, error };
  };

  const needsMfa = useCallback(() => {
    if (!profile) return false;
    if (profile.is_platform_admin) return !mfaVerified;
    return false;
  }, [profile, mfaVerified]);

  const value = {
    session,
    user,
    profile,
    loading,
    mfaVerified,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    verifyRecoveryToken,
    exchangeRecoveryCode,
    fetchProfile,
    mfaEnroll,
    mfaChallenge,
    mfaVerify,
    mfaUnenroll,
    needsMfa,
    checkMfaStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
